import type { EventDraft, RetreatEvent } from '../types/events'
import {
  getAccessToken,
  SUPABASE_PUBLISHABLE_KEY,
  SUPABASE_URL,
  SupabaseError,
  supabaseFetch,
} from './supabase'

interface EventRow {
  id: string
  tagline: string | null
  title: string
  subtitle: string | null
  performers: string | null
  event_date: string
  doors_time: string | null
  show_time: string | null
  address: string | null
  poster_url: string | null
  poster_alt: string | null
  published: boolean
  created_at: string | null
  updated_at: string | null
}

const POSTER_BUCKET = 'posters'

function fromRow(row: EventRow): RetreatEvent {
  return {
    id: row.id,
    tagline: row.tagline,
    title: row.title,
    subtitle: row.subtitle,
    performers: row.performers,
    eventDate: row.event_date,
    doorsTime: row.doors_time,
    showTime: row.show_time,
    address: row.address,
    posterUrl: row.poster_url,
    posterAlt: row.poster_alt,
    published: row.published,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

function toRow(draft: EventDraft): Omit<EventRow, 'id' | 'created_at' | 'updated_at'> {
  return {
    tagline: draft.tagline,
    title: draft.title,
    subtitle: draft.subtitle,
    performers: draft.performers,
    event_date: draft.eventDate,
    doors_time: draft.doorsTime,
    show_time: draft.showTime,
    address: draft.address,
    poster_url: draft.posterUrl,
    poster_alt: draft.posterAlt,
    published: draft.published,
  }
}

/** Public: published events from `today` onward, soonest first. */
export async function listPublishedEvents(today: string): Promise<RetreatEvent[]> {
  const response = await supabaseFetch(
    `/rest/v1/events?select=*&published=eq.true&event_date=gte.${today}&order=event_date.asc`,
  )
  const rows = (await response.json()) as EventRow[]
  return rows.map(fromRow)
}

/** Admin: every event including drafts and past shows. */
export async function listAllEvents(): Promise<RetreatEvent[]> {
  const response = await supabaseFetch(
    '/rest/v1/events?select=*&order=event_date.asc',
    {},
    { auth: true },
  )
  const rows = (await response.json()) as EventRow[]
  return rows.map(fromRow)
}

const WRITE_HEADERS = {
  'Content-Type': 'application/json',
  Prefer: 'return=representation',
}

function firstRowOrThrow(rows: EventRow[]): RetreatEvent {
  if (rows.length === 0) {
    throw new SupabaseError(
      'That change wasn’t saved — your account may not have permission to edit events.',
      403,
      'not_editor',
    )
  }
  return fromRow(rows[0])
}

export async function createEvent(draft: EventDraft): Promise<RetreatEvent> {
  const response = await supabaseFetch(
    '/rest/v1/events',
    { method: 'POST', headers: WRITE_HEADERS, body: JSON.stringify(toRow(draft)) },
    { auth: true },
  )
  return firstRowOrThrow((await response.json()) as EventRow[])
}

export async function updateEvent(id: string, draft: EventDraft): Promise<RetreatEvent> {
  const response = await supabaseFetch(
    `/rest/v1/events?id=eq.${encodeURIComponent(id)}`,
    { method: 'PATCH', headers: WRITE_HEADERS, body: JSON.stringify(toRow(draft)) },
    { auth: true },
  )
  return firstRowOrThrow((await response.json()) as EventRow[])
}

export async function deleteEvent(id: string): Promise<void> {
  await supabaseFetch(
    `/rest/v1/events?id=eq.${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { auth: true },
  )
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/** Uploads a prepared poster and resolves with its public URL. */
export async function uploadPoster(
  blob: Blob,
  onProgress: (percent: number) => void,
): Promise<string> {
  const token = await getAccessToken()
  if (!token) {
    throw new SupabaseError('Your sign-in has ended. Please sign in again.', 401)
  }

  const path = `${new Date().toISOString().slice(0, 10)}-${randomId()}.jpg`

  return new Promise<string>((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('POST', `${SUPABASE_URL}/storage/v1/object/${POSTER_BUCKET}/${path}`)
    request.setRequestHeader('apikey', SUPABASE_PUBLISHABLE_KEY)
    request.setRequestHeader('Authorization', `Bearer ${token}`)
    request.setRequestHeader('Content-Type', blob.type || 'image/jpeg')

    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100))
    }
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve(`${SUPABASE_URL}/storage/v1/object/public/${POSTER_BUCKET}/${path}`)
        return
      }
      let message = 'The poster couldn’t be uploaded. Please try again.'
      try {
        const body = JSON.parse(request.responseText) as { message?: string }
        if (body.message) message = `The poster couldn’t be uploaded: ${body.message}`
      } catch {
        // Keep the generic message.
      }
      reject(new SupabaseError(message, request.status))
    }
    request.onerror = () =>
      reject(
        new SupabaseError(
          'The upload was interrupted. Check your connection and try again.',
          0,
          'network',
        ),
      )
    request.send(blob)
  })
}
